// ImageControls.js
import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  TextField,
  FormControlLabel,
  Switch
} from '@mui/material';
import apiClient from '../utils/apiUtils';

export const imageModels = [
  { value: 'fal-ai/sana', label: 'Sana', price: 0.001, description: 'Very Fast, very low cost, average quality', capability: 'low', recommendedDimensions: '512x512' },
  { value: 'fal-ai/flux/schnell', label: 'FLUX.1 Schnell', price: 0.003, description: 'Fast generation, low cost, good quality', capability: 'low', recommendedDimensions: '512x512' },
  { value: 'fal-ai/flux/dev', label: 'FLUX.1 Dev', price: 0.025, description: 'Low cost high quality images', capability: 'high', recommendedDimensions: '1024x1024' },
  { value: 'fal-ai/recraft-v3', label: 'Recraft V3', price: 0.04, description: 'High-quality creative images', capability: 'medium', recommendedDimensions: '1024x1024' },
  { value: 'fal-ai/recraft-20b', label: 'Recraft 20b', price: 0.022, description: 'High quality images, ability to add text and logos', capability: 'medium', recommendedDimensions: '1024x1024' },
  { value: 'fal-ai/flux-pro/v1.1-ultra', label: 'FLUX 1.1 Pro Ultra', price: 0.06, description: 'Professional quality, up to 2k resolution', capability: 'high', recommendedDimensions: '2048x2048' },
  { value: 'fal-ai/ideogram/v2', label: 'Ideogram V2.0', price: 0.08, description: 'High quality visuals, posters, and logos with accurate text', capability: 'high', recommendedDimensions: '1024x1024' },
  { value: 'fal-ai/ideogram/v2a/turbo', label: 'Ideogram V2.0a Turbo', price: 0.025, description: 'Accelerated Ideogram V2.0a', capability: 'medium', recommendedDimensions: '1024x1024' }
];

const dimensions = [
  { value: 'square_hd', label: 'Square HD (1024×1024)', description: 'High definition square format' },
  { value: 'square', label: 'Square (512×512)', description: 'Standard square format' },
  { value: 'portrait_4_3', label: 'Portrait 4:3 (768×1024)', description: 'Standard portrait format' },
  { value: 'portrait_16_9', label: 'Portrait 16:9 (576×1024)', description: 'Widescreen portrait format' },
  { value: 'landscape_4_3', label: 'Landscape 4:3 (1024×768)', description: 'Standard landscape format' },
  { value: 'landscape_16_9', label: 'Landscape 16:9 (1024×576)', description: 'Widescreen landscape format' }
];

const ImageControls = ({
  model,
  setModel,
  dimension,
  setDimension,
  style,
  setStyle,
  disabled = false
}) => {
  console.log('ImageControls rendering with props:', { model, dimension, style, disabled });
  
  // Always assume we have a key for now to simplify debugging
  const [hasFalKey, setHasFalKey] = useState(true);
  const [localStyle, setLocalStyle] = useState(style || 'photo');

  // Initialize model if not set
  useEffect(() => {
    if (!model) {
      setModel('fal-ai/flux/schnell');
    }
  }, [model, setModel]);

  // Initialize style if not set
  useEffect(() => {
    if (!style) {
      setLocalStyle('photo');
    }
  }, [style]);

  const imageStyles = [
    { value: 'photo', label: 'Photorealistic' },
    { value: 'illustration', label: 'Digital Illustration' },
    { value: 'vector', label: 'Vector Style' },
    { value: 'minimalist', label: 'Minimalist' },
    { value: '3d', label: '3D Render' }
  ];

  const handleModelChange = (e) => {
    const newModel = e.target.value;
    console.log('Setting new model:', newModel);
    setModel(newModel);
  };

  const handleStyleChange = (e) => {
    const newStyle = e.target.value;
    console.log('Setting new style:', newStyle);
    if (setStyle) {
      setStyle(newStyle);
    } else {
      setLocalStyle(newStyle);
    }
  };

  const selectStyles = {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.12)',
      transition: 'all 0.2s ease'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(59, 130, 246, 0.5)'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#3B82F6',
      borderWidth: '1px'
    },
    '& .MuiSelect-select': {
      padding: '10px 14px'
    },
    borderRadius: '10px',
    fontSize: '0.875rem'
  };

  const labelStyles = {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#4B5563',
    '&.Mui-focused': {
      color: '#3B82F6'
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControl fullWidth size="small">
          <InputLabel sx={labelStyles}>Image Model</InputLabel>
          <Select
            value={model || 'fal-ai/flux/schnell'}
            label="Image Model"
            onChange={handleModelChange}
            disabled={disabled}
            sx={selectStyles}
          >
            {imageModels.map((modelOption) => (
              <MenuItem 
                key={modelOption.value} 
                value={modelOption.value} 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'flex-start', 
                  py: 1,
                  '&:hover': {
                    bgcolor: 'rgba(59, 130, 246, 0.04)'
                  },
                  '&.Mui-selected': {
                    bgcolor: 'rgba(59, 130, 246, 0.08)',
                    '&:hover': {
                      bgcolor: 'rgba(59, 130, 246, 0.12)'
                    }
                  }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{modelOption.label}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>${modelOption.price.toFixed(3)}/image</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.75rem' }}>{modelOption.description}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth size="small">
          <InputLabel sx={labelStyles}>Image Style</InputLabel>
          <Select
            value={style || localStyle}
            label="Image Style"
            onChange={handleStyleChange}
            disabled={disabled}
            sx={selectStyles}
          >
            {imageStyles.map((styleOption) => (
              <MenuItem 
                key={styleOption.value} 
                value={styleOption.value}
                sx={{ 
                  py: 1,
                  '&:hover': {
                    bgcolor: 'rgba(59, 130, 246, 0.04)'
                  },
                  '&.Mui-selected': {
                    bgcolor: 'rgba(59, 130, 246, 0.08)',
                    '&:hover': {
                      bgcolor: 'rgba(59, 130, 246, 0.12)'
                    }
                  }
                }}
              >
                {styleOption.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth size="small">
          <InputLabel sx={labelStyles}>Image Dimensions</InputLabel>
          <Select
            value={dimension}
            label="Image Dimensions"
            onChange={(e) => setDimension(e.target.value)}
            disabled={disabled}
            sx={selectStyles}
          >
            {dimensions.map((dim) => (
              <MenuItem 
                key={dim.value} 
                value={dim.value} 
                sx={{ 
                  py: 1,
                  '&:hover': {
                    bgcolor: 'rgba(59, 130, 246, 0.04)'
                  },
                  '&.Mui-selected': {
                    bgcolor: 'rgba(59, 130, 246, 0.08)',
                    '&:hover': {
                      bgcolor: 'rgba(59, 130, 246, 0.12)'
                    }
                  }
                }}
              >
                {dim.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default ImageControls;